import { useTaskboard } from '../lib/taskboardContext.js'
import { dueMeta, tsToDate, fmtDate } from '../lib/format.js'
import { dayDelta, isDueWithinDays } from '../lib/metrics.js'
import { serializeRoute } from '../lib/hashRoute.js'
import Avatar from '../components/Avatar.jsx'
import { Chip } from '../ds/index.js'

// "My Tasks" destination — your assigned tasks across every project, grouped by
// due date (Asana/Todoist convention).
function bucketOf(date, done, today) {
  if (done) return 'done'
  if (!date) return 'nodate'
  const diff = dayDelta(date, today)
  if (diff < 0) return 'overdue'
  if (diff === 0) return 'today'
  if (diff <= 7) return 'upcoming'
  return 'later'
}

const GROUPS = [
  { key: 'overdue', label: 'Overdue', dot: 'var(--color-danger)' },
  { key: 'today', label: 'Today', dot: 'var(--color-gold)' },
  { key: 'upcoming', label: 'Next 7 days', dot: '#4A7CC9' },
  { key: 'later', label: 'Later', dot: '#7A7568' },
  { key: 'nodate', label: 'No due date', dot: '#7A7568' },
  { key: 'done', label: 'Completed', dot: '#3DB89A' },
]

// `bucket` carries two kinds of value from the URL. A GROUPS key narrows the
// view to that one group. The special value 'duesoon' is instead a *window*
// filter — it trims the task list to the next 14 days and then lets the normal
// groups render inside it, because a 14-day window straddles today/upcoming/
// later and the groups are an exclusive partition that must not double-count.
const DUE_SOON_DAYS = 14
const FILTER_LABELS = {
  duesoon: `Due in the next ${DUE_SOON_DAYS} days`,
  ...Object.fromEntries(GROUPS.map((g) => [g.key, g.label])),
}

export default function MyTasksView({ tasks, projects, search, bucket = null, today = new Date() }) {
  const { labelsById, statusesById, toggleDone, openTaskInProject } = useTaskboard()
  const projById = Object.fromEntries(projects.map((p) => [p.id, p]))
  const validBucket = bucket && (bucket === 'duesoon' || GROUPS.some((g) => g.key === bucket)) ? bucket : null
  const isWindow = validBucket === 'duesoon'

  let list = tasks
  if (search && search.trim()) {
    const q = search.trim().toLowerCase()
    list = list.filter((t) => `${t.title} ${t.description || ''}`.toLowerCase().includes(q))
  }
  if (isWindow) list = list.filter((t) => isDueWithinDays(t, statusesById, DUE_SOON_DAYS, today))

  const grouped = {}
  for (const t of list) {
    const done = !!statusesById[t.status]?.done
    const b = bucketOf(tsToDate(t.dueDate), done, today)
    ;(grouped[b] ||= []).push(t)
  }

  const visibleGroups = GROUPS.filter((g) => !validBucket || isWindow || g.key === validBucket)
  const total = visibleGroups.reduce((n, g) => n + (grouped[g.key]?.length || 0), 0)
  const filterLabel = validBucket ? FILTER_LABELS[validBucket] : null

  return (
    <div>
      {filterLabel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 2px 14px' }}>
          <span className="font-mono" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--color-muted)' }}>Filtered</span>
          <span style={{ fontSize: 13, color: 'var(--color-white)' }}>{filterLabel}</span>
          <a
            href={'#' + serializeRoute({ type: 'mytasks' })}
            className="font-mono"
            style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--color-gold)', textDecoration: 'none' }}
          >
            Show all
          </a>
        </div>
      )}
      {total === 0 && (
        <div style={{ fontSize: 14, color: 'var(--text-dim)', padding: '40px 0' }}>
          {filterLabel
            ? (search && search.trim()
                ? `No tasks match "${search.trim()}" within this filter.`
                : 'No tasks match this filter.')
            : `Nothing assigned to you${search ? ' matches your search' : ' yet'}.`}
        </div>
      )}
      {visibleGroups.map((g) => {
        const items = grouped[g.key]
        if (!items || !items.length) return null
        return (
          <div key={g.key} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 2px' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.dot, flexShrink: 0 }} />
              <span className="font-display" style={{ fontSize: 16, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--color-white)' }}>{g.label}</span>
              <span className="font-mono" style={{ fontSize: 12, color: 'var(--color-muted)' }}>{items.length}</span>
            </div>
            <div style={{ border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-lg)', background: 'var(--color-panel-dark)', overflow: 'hidden', marginTop: 6 }}>
              {items.map((t) => {
                const done = !!statusesById[t.status]?.done
                const meta = dueMeta(tsToDate(t.dueDate), done)
                const proj = projById[t.projectId]
                const rowLabels = (t.labelIds || []).filter((id) => labelsById[id])
                // On a phone the trailing block is a whole second line, so only
                // render it when there is something to put there.
                const hasMeta = rowLabels.length > 0 || !!proj || meta.hasDue
                return (
                  // Indent override: this view's checkbox is 17px, not
                  // ListView's 22px, so the wrapped metadata line needs
                  // 17 + the row's 14px gap to start under the title.
                  <div
                    key={t.id}
                    className="tb-taskrow"
                    onClick={() => openTaskInProject(t.projectId, t.id)}
                    style={{ '--tb-taskrow-indent': '31px', padding: '12px 15px', borderBottom: '1px solid var(--surface-row)', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(200,168,75,.04)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div
                      onClick={(e) => { e.stopPropagation(); toggleDone(t) }}
                      style={{ width: 17, height: 17, borderRadius: '50%', border: '1.5px solid ' + (done ? 'var(--color-gold)' : '#3a3527'), background: done ? 'var(--color-gold)' : 'transparent', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {done && <span style={{ fontSize: 10, lineHeight: 1, fontWeight: 700, color: 'var(--color-bg-dark)' }}>✓</span>}
                    </div>
                    <span className="tb-taskrow-title" style={{ fontSize: 14, color: done ? 'var(--text-dim)' : 'var(--color-white)', textDecoration: done ? 'line-through' : 'none' }}>{t.title}</span>
                    {hasMeta && (
                      <div className="tb-taskrow-meta">
                        <div className="tb-taskrow-chips">
                          {rowLabels.map((id) => <Chip key={id} variant="solid" color={labelsById[id].color}>{labelsById[id].name}</Chip>)}
                        </div>
                        {/* No longer cn-hide-sm. This is a cross-project list,
                            so the project is the row's most useful context; it
                            was hidden only because the old single-line row had
                            no space for it. The wrapped second line does. */}
                        {proj && (
                          <span style={{ fontSize: 11, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{proj.name}</span>
                        )}
                        {meta.hasDue && (
                          <span style={{ color: meta.overdue ? 'var(--color-danger)' : 'var(--color-muted)', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'var(--font-mono)' }}>{fmtDate(tsToDate(t.dueDate))}</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
